// 2026-10-15：六类目录持久保存展示快照，隔离环境/身份/权限并保护失败、并发和离页请求；不缓存交易接口。
import {onUnmounted,ref,watch,Ref} from 'vue'
import store from '@/store'
import md5 from 'md5'

export const CATALOG_CACHE_POLICY={ttl:24*60*60*1000,maxEntries:40,maxBytes:1024*1024}
const storageKey='landiyun.catalog.v1'
type Entry={key:string;scope:string;saved:number;used:number;items:unknown[];total:number}
const pending=new Map<string,Promise<any>>()
const copy=<T>(value:T):T=>JSON.parse(JSON.stringify(value))
export function stableCatalogKey(value:any):string {
  if(Array.isArray(value))return '['+value.map(stableCatalogKey).join(',')+']'
  if(value && typeof value==='object')return '{'+Object.keys(value).sort().filter(key=>value[key]!==undefined)
    .map(key=>JSON.stringify(key)+':'+stableCatalogKey(value[key])).join(',')+'}'
  return JSON.stringify(value)
}
export function catalogIdentity():string {
  const getters=store.getters,user=getters.userInfo || {}
  // 2026-10-15：只存不可逆身份指纹，不把token、个人资料或权限响应写入缓存；会员自然到期也改变身份。
  return md5(stableCatalogKey({api:process.env.VUE_APP_BASE_API,org:getters.orgId,customer:user.id || 0,
    session:getters.token || '',permissions:getters.previliges || {},visible:getters.vipVisibleFunctions || {},
    allowed:getters.vipAllowedFunctions || {},levels:getters.levels || [],level:user.executeLevel,expires:user.levelExpired,
    memberActive:Number(user.levelExpired)>Date.now(),orgPermissions:getters.orgPrivileges || {}}))
}
function prune(entries:Entry[],now:number):Entry[] {
  const valid=entries.filter(entry=>entry && typeof entry.key==='string' && typeof entry.scope==='string'
    && Number.isFinite(entry.saved) && Number.isFinite(entry.used) && entry.saved<=now && now-entry.saved<CATALOG_CACHE_POLICY.ttl
    // 2026-10-15：损坏行整份快照拒用，防止null/数组/非法图片值进入卡片模板；网络读取照常继续。
    && Array.isArray(entry.items) && entry.items.every(item=>item && typeof item==='object' && !Array.isArray(item)
      && ['number','string'].includes(typeof (item as any).id) && Number.isFinite(Number((item as any).id)) && Number((item as any).id)>0
      && ((item as any).url==null || typeof (item as any).url==='string')) && Number.isFinite(entry.total) && entry.total>=0)
    .sort((a,b)=>b.used-a.used).slice(0,CATALOG_CACHE_POLICY.maxEntries)
  while(valid.length && JSON.stringify(valid).length*2>CATALOG_CACHE_POLICY.maxBytes)valid.pop()
  return valid
}
function readEntries():Entry[] {
  try {
    const data=JSON.parse(window.localStorage.getItem(storageKey) || '[]')
    const valid=prune(Array.isArray(data)?data:[],Date.now())
    try{window.localStorage.setItem(storageKey,JSON.stringify(valid))}catch{/* 已有可读快照不因写入配额不足而丢失。 */}
    return valid
  }catch{return []}
}
function writeEntry(entry:Entry):void {
  let entries=prune([entry,...readEntries().filter(item=>item.key!==entry.key && item.scope===entry.scope)],Date.now())
  while(entries.length){
    try{window.localStorage.setItem(storageKey,JSON.stringify(entries));return}
    catch{entries=entries.slice(0,-1)}
  }
}
export function createCatalogLoader<T>(catalog:string,items:Ref<T[]>,total:Ref<number>){
  const loading=ref(false),error=ref(''),cached=ref(false)
  let sequence=0,disposed=false,activeKey='',shownKey='',lastRetry:(()=>Promise<void>)|undefined
  const clear=()=>{items.value=[];total.value=0;shownKey='';cached.value=false}
  const invalidate=()=>{sequence++;activeKey='';shownKey='';loading.value=false;error.value='';cached.value=false;lastRetry=undefined}
  const stop=watch(catalogIdentity,()=>{
    const refresh=lastRetry
    invalidate();clear()
    // 2026-10-15：同页登录/切换权限后重新读取当前查询，避免清除旧身份快照后永久空白。
    if(refresh)void Promise.resolve().then(()=>{if(!disposed)return refresh()})
  },{flush:'sync'})
  onUnmounted(()=>{disposed=true;invalidate();stop()})
  const run=async(query:Record<string,any>,request:(snapshot:any)=>Promise<any>,apply:(response:any)=>void):Promise<void>=>{
    if(disposed)return
    const scope=catalogIdentity(),snapshot=copy({...query,orgId:store.getters.orgId,customerId:store.getters.userInfo?.id || undefined})
    const key=scope+':'+catalog+':'+stableCatalogKey(snapshot),ticket=++sequence
    const current=()=>!disposed && ticket===sequence && scope===catalogIdentity() && activeKey===key
    activeKey=key;error.value='';loading.value=true
    lastRetry=()=>run(snapshot,request,apply)
    const saved=readEntries().find(entry=>entry.key===key && entry.scope===scope)
    if(saved){items.value=copy(saved.items) as T[];total.value=saved.total;shownKey=key;cached.value=true;writeEntry({...saved,used:Date.now()})}
    else if(shownKey!==key)clear()
    try {
      let work=pending.get(key)
      if(!work){
        work=Promise.resolve().then(()=>{if(scope!==catalogIdentity())throw Error('Catalog identity changed');return request(snapshot)})
        pending.set(key,work)
        // 2026-10-15：共享在途读取，成功或失败都释放；不保留失败Promise妨碍重试。
        void work.then(()=>{if(pending.get(key)===work)pending.delete(key)},()=>{if(pending.get(key)===work)pending.delete(key)})
      }
      const response=await work
      if(!current())return
      if(!Array.isArray(response?.data?.list) || !Number.isFinite(Number(response.data.total)))throw Error('Invalid catalog response')
      apply(copy(response));shownKey=key;cached.value=false
      writeEntry({key,scope,saved:Date.now(),used:Date.now(),items:copy(items.value),total:total.value})
    }catch {
      if(current())error.value=items.value.length?'刷新失败，正在显示上次内容，请重试。':'商品加载失败，请检查网络后重试。'
    }finally{if(current())loading.value=false}
  }
  return {run,loading,error,cached,invalidate,retry:()=>lastRetry?.()}
}
